// Realtime contract for voice-call messages: inbound rings surface for every
// agent in a conference-mode inbox, even when the inbox is not yet hydrated in
// the store (boot window). Outbound calls are broadcast to all inbox members
// too, but must surface only on the initiating user's own tabs; ringing them
// elsewhere would auto-join other agents via FloatingCallWidget's outbound
// watcher.
import {
  currentAccountIdFromUrl,
  NON_OFFERABLE_STATUSES,
} from 'dashboard/helper/voice';

const isVoiceCallMessage = data =>
  data?.content_type === 12 || data?.content_type === 'voice_call';

const isCurrentUserOutbound = (store, data, payload) => {
  if (!payload.isOutbound) return false;
  const currentUserId = store.getters.getCurrentUserID;
  const performerId = data?.performer?.id;
  return !!performerId && performerId === currentUserId;
};

// Exported (not forked) so the boot-hydrate adapter can build the same offer
// shape from a different source without the two definitions drifting.
export const buildIncomingCallPayload = (data, inboxFromStore) => {
  const contentData = data.content_attributes?.data || {};
  return {
    callSid: contentData.call_sid,
    conversationId: data.display_id || data.id,
    accountId: data.account_id,
    inboxId: data.inbox_id || inboxFromStore?.id,
    inboxName: data.meta?.inbox?.name || inboxFromStore?.name,
    contactName: data.meta?.sender?.name || 'Unknown Caller',
    phoneNumber: data.meta?.sender?.phone_number,
    isOutbound: contentData.call_direction === 'outbound',
    callDirection: contentData.call_direction,
    // Must be persisted so the TERMINAL_STATUSES hydrate guard in calls.js
    // can drop entries for calls that have already ended.
    status: contentData.status,
  };
};

// Adapts an active-calls endpoint row (see Voice::ActiveCallsFinder) into the
// canonical offer shape used by setIncomingCall / setRecoverableCall on boot.
// conference_sid and call_started_at are carried through for the in-progress
// rejoin path.
export const fromActiveCallRow = (row, { inboxName } = {}) => ({
  callSid: row.call_sid,
  conversationId: row.conversation_id,
  status: row.call_status,
  callDirection: row.call_direction,
  isOutbound: row.call_direction === 'outbound',
  contactName: row.contact?.name,
  phoneNumber: row.contact?.phone,
  inboxId: row.inbox_id,
  inboxName,
  accountId: currentAccountIdFromUrl(),
  conference_sid: row.conference_sid,
  call_started_at: row.call_started_at,
  // Carried so a refresh mid-consult restores the consult panel: rejoinCall spreads the whole
  // recovered call into callMeta, so this rides into the rebuilt activeCall (persistActiveCall
  // then drops it - the server row is authoritative, a stale persisted consult must not drive UI).
  consult: row.consult,
});

export const handleVoiceMessageUpdated = (app, data) => {
  try {
    if (!isVoiceCallMessage(data)) {
      return;
    }

    const store = app.$store;
    const status = data?.content_attributes?.data?.status;
    const callSid = data?.content_attributes?.data?.call_sid;
    const conversationId = data?.conversation_id;
    const consult = data?.content_attributes?.data?.consult;

    // A consult push carries NO status (the consult broadcaster never touches status), so it must
    // be applied INDEPENDENTLY of the status guard below - else Merge never becomes clickable and
    // the patient sits on hold. It targets the active-call row by callSid; a null consult clears it.
    if (callSid && consult !== undefined) {
      store.dispatch('calls/handleConsultUpdated', { callSid, consult });
    }

    if (!callSid || !status) {
      return;
    }

    store.dispatch('calls/handleCallStatusChanged', {
      callSid,
      status,
    });

    try {
      store.commit('UPDATE_CONVERSATION_CALL_STATUS', {
        conversationId,
        callStatus: status,
      });
    } catch (error) {
      // ignore commit errors
    }

    // A second concurrent ring must be OFFERED, not dropped: only a non-ringing
    // status short-circuits here. The store's setIncomingCall dedups by callSid,
    // so a repeat event for the same call is harmless, and the incoming-call
    // queue lets a free agent pick up the second caller.
    if (status !== 'ringing') {
      return;
    }

    const conversationGetter =
      store.getters['conversations/getConversationById'];
    const conv = conversationGetter?.(conversationId);
    const inboxGetter = store.getters['inboxes/getInbox'];
    const inboxFromStore = inboxGetter?.(conv?.inbox_id || data?.inbox_id);

    const payload = buildIncomingCallPayload(
      {
        ...data,
        display_id: conversationId,
        inbox_id: conv?.inbox_id || data?.inbox_id,
        account_id: conv?.account_id || data?.account_id,
        meta: conv?.meta || data?.meta,
      },
      inboxFromStore
    );
    // Inbound rings surface for everyone in a conference-mode inbox; outbound
    // calls surface only on the initiating user's tabs. A non-offerable status
    // (terminal or voice_mailing) is never offered as a ring.
    if (
      (!payload.isOutbound &&
        !NON_OFFERABLE_STATUSES.includes(status) &&
        !inboxFromStore?.voicemails_only &&
        payload.callSid) ||
      isCurrentUserOutbound(store, data, payload)
    ) {
      store.dispatch('calls/setIncomingCall', payload);
    }
  } catch (error) {
    // Keep the realtime handler resilient (never crash the websocket subscription),
    // but don't hide the failure - a swallowed throw here silently drops a real ring.
    // eslint-disable-next-line no-console
    console.error('voice realtime updated failed', error);
  }
};

export const handleVoiceMessageCreated = (app, data) => {
  try {
    if (!isVoiceCallMessage(data)) {
      return;
    }

    const store = app.$store;
    const status = data?.content_attributes?.data?.status;
    const callSid = data?.content_attributes?.data?.call_sid;
    const conversationId = data?.conversation_id;

    if (!callSid) {
      return;
    }

    const conversationGetter =
      store.getters['conversations/getConversationById'];
    const conv = conversationGetter?.(conversationId);
    const inboxGetter = store.getters['inboxes/getInbox'];
    const inboxFromStore = inboxGetter?.(conv?.inbox_id || data?.inbox_id);

    const payload = buildIncomingCallPayload(
      {
        ...data,
        display_id: conversationId,
        inbox_id: conv?.inbox_id || data?.inbox_id,
        account_id: conv?.account_id || data?.account_id,
        meta: conv?.meta || data?.meta,
      },
      inboxFromStore
    );

    // Only surface an incoming-call UI for a ringing inbound call; a non-ringing voice message
    // (e.g. a voicemail being recorded) must not ring, especially before the inbox is hydrated.
    // Outbound calls surface only on the initiating user's tabs. A second concurrent ring is
    // QUEUED (setIncomingCall dedups by callSid), so we no longer gate on an existing ringing
    // or in-call state - dropping it there sent the second caller to voicemail.
    if (
      (status === 'ringing' &&
        !NON_OFFERABLE_STATUSES.includes(status) &&
        !payload.isOutbound &&
        !inboxFromStore?.voicemails_only &&
        payload.callSid) ||
      isCurrentUserOutbound(store, data, payload)
    ) {
      store.dispatch('calls/setIncomingCall', payload);
    }

    if (status) {
      store.dispatch('calls/handleCallStatusChanged', {
        callSid,
        status,
      });
    }
  } catch (error) {
    // Keep the realtime handler resilient (never crash the websocket subscription),
    // but don't hide the failure - a swallowed throw here silently drops a real ring.
    // eslint-disable-next-line no-console
    console.error('voice realtime created failed', error);
  }
};
